import { Controller } from "@hotwired/stimulus";
import {
  type MultipleSelectInstance,
  multipleSelect,
} from "multiple-select-vanilla";

/**
 * Manages multiple instances of the 'multiple-select-vanilla' library
 * within its scope. It initializes the library on all elements with the
 * class 'multiple-select' upon connection and destroys the instances
 * upon disconnection.
 *
 * @example
 * <div data-controller="selects">
 *   <select class="multiple-select" multiple name="options1[]">
 *     <option value="1">Option 1.1</option>
 *     <option value="2">Option 1.2</option>
 *   </select>
 *
 *   <select class="multiple-select" multiple name="options2[]">
 *     <option value="a">Option 2.a</option>
 *     <option value="b">Option 2.b</option>
 *   </select>
 * </div>
 */
class SelectsController extends Controller<HTMLElement> {
  static values = { submitShortcuts: { type: Boolean, default: true } };
  declare readonly submitShortcutsValue: boolean;

  private msInstances: MultipleSelectInstance[] = [];

  connect() {
    const selectElements =
      this.element.querySelectorAll<HTMLSelectElement>(".multiple-select");

    if (selectElements.length === 0) {
      console.warn(
        'No select elements with class "multiple-select" found within the selects controller scope.',
      );
      return;
    }

    selectElements.forEach((selectElement) => {
      const instance = multipleSelect(selectElement, {
        // Add any default options for multiple-select-vanilla here
        // For example:
        placeholder: "Filter by accounts (All)",
        // Add more options as needed based on the library's documentation
        renderOptionLabelAsHtml: true, // without this flag, html code will be showing as plain text
        displayDelimiter: " ",
        // Keep the selected account labels visible. CSS constrains the selector
        // itself and lets the selected labels scroll horizontally on small screens.
        minimumCountSelected: selectElement.options.length + 1,
        textTemplate: (el) => {
          return `<span>${el.innerHTML}</span>`;
        },
        selectAll: false,
        onOpen: () => {
          selectElement.nextElementSibling
            ?.querySelector<HTMLButtonElement>(
              "[data-account-shortcut]:not(:disabled)",
            )
            ?.focus();
        },
      }) as MultipleSelectInstance;

      if (instance) {
        instance.close();

        this.msInstances.push(instance);
        this.addAccountShortcuts(selectElement, instance);
      }
    });
  }

  private addAccountShortcuts(
    select: HTMLSelectElement,
    instance: MultipleSelectInstance,
  ) {
    const template = this.element.querySelector<HTMLTemplateElement>(
      "template[data-account-shortcuts]",
    );
    // These selectors are the vendor's generated DOM contract.
    const parent = select.nextElementSibling;
    const drop = parent?.querySelector<HTMLElement>(".ms-drop");
    const choice = parent?.querySelector<HTMLButtonElement>(".ms-choice");
    const label = choice?.querySelector<HTMLSpanElement>("span");
    if (!template || !drop || !choice || !label) return;

    const shortcuts = template.content.cloneNode(true) as DocumentFragment;
    const buttons = Array.from(
      shortcuts.querySelectorAll<HTMLButtonElement>("[data-account-shortcut]"),
    );
    const options = Array.from(select.options);
    const valuesFor = (shortcut: string) =>
      options
        .filter((option) =>
          shortcut === "all" ||
          option.dataset.inBudget === String(shortcut === "in-budget"),
        )
        .map((option) => option.value);

    const updateLabel = () => {
      const selected = Array.from(select.selectedOptions).map(
        (option) => option.value,
      );
      const all = selected.length === 0 || selected.length === options.length;
      let summary: string | undefined;
      for (const button of buttons) {
        const shortcut = button.dataset.accountShortcut!;
        const values = valuesFor(shortcut);
        const active =
          shortcut === "all"
            ? all
            : !all &&
              values.length === selected.length &&
              values.every((value) => selected.includes(value));
        button.setAttribute("aria-pressed", String(active));
        if (active) summary = button.textContent ?? undefined;
      }
      if (summary) label.textContent = summary;
      choice.setAttribute(
        "aria-label",
        `${select.getAttribute("aria-label")}: ${label.textContent}`,
      );
    };
    for (const button of buttons) {
      const values = valuesFor(button.dataset.accountShortcut!);
      // No native account selection means "all", never an empty result.
      // Disable empty shortcuts rather than accidentally showing all accounts.
      button.disabled = values.length === 0;
      button.addEventListener("keydown", (event) => {
        // Let native buttons handle activation and Tab, not the vendor listbox.
        if (["Enter", " ", "Tab"].includes(event.key)) event.stopPropagation();
      });
      button.addEventListener("click", () => {
        instance.setSelects(values, "value", true);
        updateLabel();
        instance.close();
        choice.focus();
        if (this.submitShortcutsValue) select.form?.requestSubmit();
      });
    }
    drop.prepend(shortcuts);
    select.addEventListener("change", updateLabel);
    this.cleanup.push(() => select.removeEventListener("change", updateLabel));
    updateLabel();
  }

  private cleanup: (() => void)[] = [];

  disconnect() {
    this.cleanup.forEach((cleanup) => cleanup());
    this.cleanup = [];
    this.msInstances.forEach((instance) => {
      if (instance) {
        instance.destroy();
      }
    });
    this.msInstances = [];
  }
}

export default SelectsController;
